import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiltersState } from 'src/domains/layouts/webview/components/filters/Filters.state';
import {
    CheckIconWrapper,
    FilterSecondaryDropdownMenuFixed,
    FilterSecondaryDropdownWrapper,
    FiltersSecondaryList,
} from 'src/domains/layouts/webview/components/filtersSecondary/FiltersSecondary.style';
import { FilterSecondary } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondary';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { FilterSecondaryDropdownOption } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondaryDropdownOption';
import { FilterSecondaryWithBadge } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondaryWithBadge';
import { FilterSecondaryWithDropdown } from 'src/domains/layouts/webview/components/filtersSecondary/FilterSecondaryWithDropdown';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { getPrimaryFiltersOrder, SportsFilterChipType, SportsPrimaryFilterId } from './UnifiedSportsFilters.state';
import { SportsTimeFilterType } from './SportsTimeFilter';
import { useUnifiedMatchesDropdownPosition } from './useUnifiedMatchesDropdownPosition';
import { MatchesFilterLabel } from './MatchesFilterLabel';
import { observer } from 'src/utils/mobx-react';

interface SportsFiltersUnifiedPrimaryRowPropsType {
    eventsListState: UnifiedSportEventsListState;
    timeFiltersState: FiltersState<SportsTimeFilterType | null>;
    sportFilterChip: SportsFilterChipType;
    onSportFilterChipChange: (chip: SportsFilterChipType) => void;
    inPlayCount: number;
    outrightsCount: number;
}

export const SportsFiltersUnifiedPrimaryRow = observer(
    'SportsFiltersUnifiedPrimaryRow',
    ({
        eventsListState,
        timeFiltersState,
        sportFilterChip,
        onSportFilterChipChange,
        inPlayCount,
        outrightsCount,
    }: SportsFiltersUnifiedPrimaryRowPropsType): JSX.Element => {
        const [isMatchesOpen, setIsMatchesOpen] = useState(false);
        const matchesRef = useRef<HTMLDivElement>(null);
        const matchesMenuRef = useRef<HTMLDivElement>(null);

        const closeMatchesMenu = useCallback((): void => {
            setIsMatchesOpen(false);
        }, []);

        const { menuPosition, updateMenuPosition } = useUnifiedMatchesDropdownPosition({
            isOpen: isMatchesOpen,
            buttonRef: matchesRef,
            menuRef: matchesMenuRef,
            onClose: closeMatchesMenu,
        });

        const isOutrightsActive = eventsListState.filter_outrights === true;
        const isTimeFilterDisabled = eventsListState.filter_inPlay === true || isOutrightsActive === true;
        const activeTimeFilter = timeFiltersState.filters.find((filter) => filter.id === eventsListState.filter_time);

        const isCompetitionActive =
            isOutrightsActive === false && eventsListState.filter_inPlay === false && sportFilterChip === 'competition';
        const isFeaturedActive =
            isOutrightsActive === false &&
            eventsListState.filter_inPlay === false &&
            eventsListState.filter_time === null &&
            sportFilterChip === 'featured';
        const isInPlayActive = isOutrightsActive === false && eventsListState.filter_inPlay === true;
        const isMatchesActive = isTimeFilterDisabled === false && eventsListState.filter_time !== null;

        const handleFeaturedClick = (): void => {
            onSportFilterChipChange('featured');
            eventsListState.clearSecondaryFilters();
            closeMatchesMenu();
        };

        const handleInPlayClick = (): void => {
            onSportFilterChipChange('featured');
            eventsListState.setFilterInPlay(true);
            closeMatchesMenu();
        };

        const handleOutrightsClick = (): void => {
            onSportFilterChipChange('featured');
            eventsListState.setFilterOutrights(true);
            closeMatchesMenu();
        };

        const handleCompetitionClick = (): void => {
            onSportFilterChipChange('competition');
            eventsListState.setFilterInPlay(false);
            eventsListState.setFilterOutrights(false);
            eventsListState.setFilterTime(null);
            closeMatchesMenu();
        };

        const handleMatchesChipClick = (): void => {
            if (isTimeFilterDisabled === true) {
                return;
            }

            if (isMatchesOpen === true) {
                closeMatchesMenu();
                return;
            }

            updateMenuPosition();
            setIsMatchesOpen(true);
        };

        const matchesLabel =
            eventsListState.filter_time !== null && typeof activeTimeFilter?.label === 'string' ? (
                activeTimeFilter.label
            ) : (
                <MatchesFilterLabel sportId={eventsListState.sport} />
            );

        const matchesMenu =
            isMatchesOpen === true && isTimeFilterDisabled === false && menuPosition !== null
                ? createPortal(
                      <FilterSecondaryDropdownMenuFixed
                          ref={matchesMenuRef}
                          top={menuPosition.top}
                          left={menuPosition.left}
                          minWidth={menuPosition.minWidth}
                          data-testid='sports-filters-matches-menu'
                      >
                          {timeFiltersState.filters.map(({ id, key, label }) => {
                              const isSelected = eventsListState.filter_time === id;

                              return (
                                  <FilterSecondaryDropdownOption
                                      key={key}
                                      isSelected={isSelected}
                                      onClick={(): void => {
                                          timeFiltersState.bindSetActiveFilterId(id)();
                                          onSportFilterChipChange('featured');
                                          closeMatchesMenu();
                                      }}
                                  >
                                      {label}
                                      {isSelected ? <CheckIconWrapper /> : null}
                                  </FilterSecondaryDropdownOption>
                              );
                          })}
                      </FilterSecondaryDropdownMenuFixed>,
                      document.body
                  )
                : null;

        const renderFilter = (filterId: SportsPrimaryFilterId): JSX.Element | null => {
            switch (filterId) {
                case 'competition':
                    return (
                        <FilterSecondary
                            key={filterId}
                            isActive={isCompetitionActive}
                            onClick={handleCompetitionClick}
                            dataTestFilterKey='competition'
                        >
                            {eventsListState.sport === 'golf' ? (
                                <I18n
                                    langKey='events.table.filter.tournamnents'
                                    defaultText='Tournaments'
                                />
                            ) : (
                                <I18n
                                    langKey='events.table.filter.competition'
                                    defaultText='Competition'
                                />
                            )}
                        </FilterSecondary>
                    );
                case 'featured':
                    return (
                        <FilterSecondary
                            key={filterId}
                            isActive={isFeaturedActive}
                            onClick={handleFeaturedClick}
                            dataTestFilterKey='featured'
                        >
                            <I18n
                                langKey='events.table.filter.featured'
                                defaultText='Featured'
                            />
                        </FilterSecondary>
                    );
                case 'matches':
                    return (
                        <FilterSecondaryDropdownWrapper
                            key={filterId}
                            ref={matchesRef}
                        >
                            <FilterSecondaryWithDropdown
                                isActive={isMatchesActive}
                                isOpen={isMatchesOpen}
                                isDisabled={isTimeFilterDisabled}
                                onClick={handleMatchesChipClick}
                                dataTestFilterKey='matches'
                            >
                                {matchesLabel}
                            </FilterSecondaryWithDropdown>
                        </FilterSecondaryDropdownWrapper>
                    );
                case 'in-play':
                    return (
                        <FilterSecondaryWithBadge
                            key={filterId}
                            badge={inPlayCount}
                            isActive={isInPlayActive}
                            onClick={handleInPlayClick}
                            dataTestFilterKey='in-play'
                        >
                            <I18n
                                langKey='events.time-options.in-play'
                                defaultText='In Play'
                            />
                        </FilterSecondaryWithBadge>
                    );
                case 'outrights':
                    return (
                        <FilterSecondaryWithBadge
                            key={filterId}
                            badge={outrightsCount}
                            isActive={isOutrightsActive}
                            onClick={handleOutrightsClick}
                            dataTestFilterKey='outrights'
                        >
                            <I18n
                                langKey='events.coupons.outrights.label'
                                defaultText='Outrights'
                            />
                        </FilterSecondaryWithBadge>
                    );
                default:
                    return null;
            }
        };

        return (
            <>
                <FiltersSecondaryList>{getPrimaryFiltersOrder(eventsListState).map(renderFilter)}</FiltersSecondaryList>

                {matchesMenu}
            </>
        );
    }
);
